"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin, Layers, X, ChevronDown, Stethoscope } from "lucide-react";
import { Country, State, City } from 'country-state-city';
import { toast } from "react-hot-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import Label from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ProfessionalTitlePicker } from "../ui/professional-title-picker";
import SingleSelectDropdown from "../ui/singleselect-dropdown";
import settingsService from "../../apis/settingsService";
import { updateProfile as updateProfileApi } from "../../apis/userApiService";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { buildEhrOptions, filterAndSortEhrList } from "../../constants/ehrList";
import { buildSpecialtyOptions } from "../../constants/specialtiesList";

// ── Searchable Combobox with free-text insert ─────────────────────────────
function SearchableCombobox({ options = [], value = "", onChange, placeholder = "Search or type...", itemLabel = "option" }) {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value ?? "");
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setInputVal(value ?? "");
    if (!value) {
      setSearchQuery("");
    }
  }, [value]);

  const optionStrings = useMemo(() => {
    const list = [];
    const seen = new Set();
    (Array.isArray(options) ? options : []).forEach(o => {
      const val = typeof o === "string" ? o : (o?.value ?? o?.label ?? "");
      if (!val) return;
      const key = val.trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        list.push(val);
      }
    });
    return list;
  }, [options]);

  const filtered = useMemo(() => {
    const q = (searchQuery || "").trim();
    if (!q) {
      return optionStrings;
    }
    return filterAndSortEhrList(optionStrings, q);
  }, [optionStrings, searchQuery]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      if (searchQuery.trim()) {
        setHighlightedIndex(filtered.length > 0 ? 0 : -1);
      } else {
        const cur = (inputVal || "").trim().toLowerCase();
        const matchIdx = filtered.findIndex(o => o.toLowerCase() === cur);
        setHighlightedIndex(matchIdx >= 0 ? matchIdx : (filtered.length > 0 ? 0 : -1));
      }
    } else {
      setHighlightedIndex(-1);
    }
  }, [open, searchQuery, filtered]);

  useEffect(() => {
    if (open && listRef.current && highlightedIndex >= 0) {
      const itemEl = listRef.current.children[highlightedIndex];
      if (itemEl && typeof itemEl.scrollIntoView === "function") {
        itemEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, open]);

  const handleSelect = (opt) => {
    setInputVal(opt);
    setSearchQuery("");
    onChange(opt);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setInputVal("");
    setSearchQuery("");
    onChange("");
    setOpen(true);
    inputRef.current?.focus();
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen(prev => {
      const next = !prev;
      if (next) {
        setSearchQuery("");
        inputRef.current?.focus();
      }
      return next;
    });
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    setSearchQuery(v);
    onChange(v);
    setOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else if (filtered.length > 0) {
        setHighlightedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else if (filtered.length > 0) {
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (open && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        handleSelect(filtered[highlightedIndex]);
      } else if (open && filtered.length > 0) {
        handleSelect(filtered[0]);
      } else if (open && inputVal.trim()) {
        handleSelect(inputVal.trim());
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setSearchQuery("");
      }
    } else if (e.key === "Tab") {
      setOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <div ref={containerRef} className="relative mt-1.5">
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          className="h-11 sm:h-11.5 w-full rounded-xl border border-slate-200 bg-white pl-4 pr-16 text-sm sm:text-base font-medium text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder={placeholder}
          value={inputVal}
          onFocus={(e) => {
            setOpen(true);
            setTimeout(() => {
              e.target?.select();
            }, 0);
          }}
          onClick={() => {
            if (!open) {
              setSearchQuery("");
              setOpen(true);
            }
          }}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        <div className="absolute right-3 flex items-center gap-1.5 text-slate-400">
          {inputVal ? (
            <button
              type="button"
              aria-label="Clear selection"
              className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              onMouseDown={handleClear}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Toggle options"
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            onClick={handleToggle}
          >
            <ChevronDown className={`h-4.5 w-4.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && filtered.length > 0 && (
        <div ref={listRef} className="absolute left-0 top-full z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1">
          {filtered.map((opt, idx) => {
            const isSelected = opt.toLowerCase() === (inputVal || "").trim().toLowerCase();
            const isHighlighted = idx === highlightedIndex;
            return (
              <button
                key={opt}
                type="button"
                className={`flex w-full items-center justify-between px-3.5 py-2 text-sm sm:text-sm text-left transition-colors cursor-pointer ${
                  isSelected && isHighlighted
                    ? "bg-blue-100 text-blue-700 font-medium"
                    : isHighlighted
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : isSelected
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : "text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                }`}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
              >
                <span>{opt}</span>
                {isSelected && <CheckCircle2 className="h-4 w-4 text-blue-600 shrink-0" />}
              </button>
            );
          })}
          {Boolean(searchQuery.trim()) && !filtered.some(f => f.toLowerCase() === inputVal.trim().toLowerCase()) && (
            <div className="border-t border-slate-100 p-1.5 bg-slate-50/70">
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-1.5 rounded-lg text-left text-xs sm:text-sm text-blue-600 hover:bg-blue-100/60 transition-colors cursor-pointer font-medium"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(inputVal.trim());
                }}
              >
                <span>Use "<strong>{inputVal.trim()}</strong>" as custom {itemLabel}</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">Custom</span>
              </button>
            </div>
          )}
        </div>
      )}
      {open && filtered.length === 0 && Boolean((inputVal || "").trim()) && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-xl p-1.5">
          <button
            type="button"
            className="flex w-full items-center justify-between px-3 py-1.5 rounded-lg text-left text-xs sm:text-sm text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer font-medium"
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelect(inputVal.trim());
            }}
          >
            <span>Use "<strong>{inputVal.trim()}</strong>" as custom {itemLabel}</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">Select</span>
          </button>
        </div>
      )}
    </div>
  );
}

const fieldDefaults = {
  title: "",
  firstName: "",
  lastName: "",
  specialty: "",
  roleTitle: "",
  companySize: "",
  ehrSystem: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  practicePartners: [],
  hpiDepthLevel: 3,
};

const toOptionStrings = (list) => {
  if (!Array.isArray(list)) return [];
  const values = list
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        const candidates = [item.value, item.label];
        const match = candidates.find((v) => typeof v === "string" && v.trim());
        return match ? match.trim() : "";
      }
      return "";
    })
    .map((v) => v.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  return Array.from(new Set(values));
};

const hpiPreviews = {
  default: {
    concise: "The patient is a 45-year-old male presenting with acute, sharp lower back pain (onset 2 days ago after lifting heavy boxes). Pain is rated 7/10, radiates to the left buttock, and worsens with bending. Denies numbness, tingling, weakness, or bowel/bladder changes. Relieved slightly by rest and ibuprofen.",
    balanced: "The patient is a 45-year-old male presenting with acute lower back pain that began two days ago after lifting heavy boxes. The pain is rated 7/10, sharp, constant, and radiates to the left buttock. It is worsened by bending and sitting, and partially relieved by rest and ibuprofen. Denies any numbness, tingling, weakness, or bowel/bladder changes.",
    detailed: "The patient is a 45-year-old male with no significant past medical history who presents to the clinic complaining of acute, sharp lower back pain that began approximately two days ago. He reports the onset occurred suddenly after he was lifting heavy cardboard boxes while moving. The pain is localized to the lumbar region, rated as a 7 out of 10 on the pain scale, and is described as sharp and constant. He notes that the pain is exacerbated by bending forward, twisting at the waist, and prolonged sitting. It is minimally relieved by rest and over-the-counter ibuprofen (400 mg every 6 hours), which reduces the pain to a 5 out of 10. He denies any associated numbness, tingling, or weakness in his lower extremities. He also denies any saddle anesthesia, changes in bowel or bladder function, fever, chills, or recent trauma other than the lifting incident."
  },
  "Cardiology": {
    concise: "The patient is a 62-year-old female presenting with episodic chest tightness and palpitations over the past week, exacerbated by climbing stairs. Relieved by rest. Denies radiation, sweating, nausea, or syncope.",
    balanced: "The patient is a 62-year-old female presenting with episodic chest tightness and palpitations over the past week. The tightness occurs with exertion (climbing stairs) and resolves with rest. Denies radiation, sweating, nausea, or syncope.",
    detailed: "The patient is a 62-year-old female with a history of hypertension and hyperlipidemia who presents with a one-week history of episodic chest tightness and palpitations. She describes the tightness as a substernal pressure, rated 4/10, occurring primarily during physical exertion such as climbing stairs, and resolving within 5-10 minutes of rest. She denies radiation of pain to the left arm or jaw. She reports no associated diaphoresis, dyspnea, nausea, vomiting, or syncope. Over-the-counter antacids have provided no relief. She has not previously experienced similar symptoms."
  },
  "Psychiatry": {
    concise: "The patient is a 28-year-old male presenting with worsening anxiety and sleep onset insomnia over the past 3 months due to increased work stress. Reports daily worry and fatigue. Denies suicidal ideation or panic attacks.",
    balanced: "The patient is a 28-year-old male presenting with worsening generalized anxiety and sleep onset insomnia over the past 3 months due to increased work stress. He reports daily worry, muscle tension, and daytime fatigue. Denies suicidal ideation or panic attacks.",
    detailed: "The patient is a 28-year-old male with no prior psychiatric history who presents with a 3-month history of worsening generalized anxiety and sleep onset insomnia. He describes experiencing persistent, daily worry regarding his job performance, stating he finds it difficult to control. Associated symptoms include muscle tension, difficulty concentrating, and significant daytime fatigue. He reports it takes him 1-2 hours to fall asleep most nights. He denies experiencing panic attacks, obsessive-compulsive behaviors, or depressive symptoms, and denies any suicidal or homicidal ideation."
  },
  "Mental Health Counseling": {
    concise: "The patient is a 28-year-old male presenting with worsening anxiety and sleep onset insomnia over the past 3 months due to increased work stress. Reports daily worry and fatigue. Denies suicidal ideation or panic attacks.",
    balanced: "The patient is a 28-year-old male presenting with worsening generalized anxiety and sleep onset insomnia over the past 3 months due to increased work stress. He reports daily worry, muscle tension, and daytime fatigue. Denies suicidal ideation or panic attacks.",
    detailed: "The patient is a 28-year-old male with no prior psychiatric history who presents with a 3-month history of worsening generalized anxiety and sleep onset insomnia. He describes experiencing persistent, daily worry regarding his job performance, stating he finds it difficult to control. Associated symptoms include muscle tension, difficulty concentrating, and significant daytime fatigue. He reports it takes him 1-2 hours to fall asleep most nights. He denies experiencing panic attacks, obsessive-compulsive behaviors, or depressive symptoms, and denies any suicidal or homicidal ideation."
  },
  "Pediatrics": {
    concise: "The patient is a 4-year-old male presenting with a 2-day history of subjective fever, rhinorrhea, and tugging at the right ear. Decreased appetite, but maintaining adequate fluid intake. Sleep is disrupted.",
    balanced: "The patient is a 4-year-old male presenting with subjective fever, rhinorrhea, and tugging at the right ear for 2 days. Appetite is slightly decreased but fluid intake is adequate. Sleep is disrupted.",
    detailed: "The patient is a 4-year-old male, accompanied by his mother, presenting with a 2-day history of nasal congestion, clear rhinorrhea, and a subjective fever. The mother reports the child has been increasingly irritable and has been frequently tugging at his right ear since yesterday. He has had a mild decrease in solid food intake but is maintaining good oral hydration with water and juice. There is no history of vomiting, diarrhea, cough, or rash. He has no history of recurrent otitis media. Immunizations are up to date."
  }
};

const BirdLogo = () => (
  <div className="mb-6 flex items-center gap-2 text-2xl font-bold tracking-tight">
    <Stethoscope className="w-8 h-8 shrink-0 text-blue-600" />
    <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent leading-none">
      VirtualScribe
    </span>
  </div>
);

export default function OnboardingForm() {
  const navigate = useNavigate();
  const { currentUser, refreshMe, loading } = useAuth();
  const profile = currentUser?.profile || {};

  const [formValues, setFormValues] = useState(() => ({
    ...fieldDefaults,
    title: profile?.title || "",
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    specialty: profile?.specialty || "",
    roleTitle: profile?.roleTitle || "",
    companySize: profile?.companySize || "",
    ehrSystem: profile?.ehrSystem || "",
    address: profile?.address || "",
    city: profile?.city || "",
    state: profile?.state || "",
    zipCode: profile?.zipCode || "",
    country: profile?.country || "",
    practicePartners: typeof profile?.practicePartners === "string"
      ? JSON.parse(profile.practicePartners)
      : Array.isArray(profile?.practicePartners)
        ? profile.practicePartners
        : [],
    hpiDepthLevel: profile?.hpiDepthLevel || 3,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState({});
  const [step, setStep] = useState(0);

  const companySizeOptions = useMemo(() => {
    const list = toOptionStrings(options.companySizes);
    return list.length > 0 ? list : ["Solo Practitioner", "2-5", "6-10", "10+"];
  }, [options.companySizes]);

  const specialtyOptions = useMemo(() => {
    const list = toOptionStrings(options.specialties);
    return buildSpecialtyOptions(list);
  }, [options.specialties]);

  const ehrOptions = useMemo(() => {
    const list = toOptionStrings(options.ehrSystems);
    return buildEhrOptions(list);
  }, [options.ehrSystems]);

  const roleOptions = useMemo(() => {
    const list = toOptionStrings(options.roles);
    return list.length > 0 ? list : ["Doctor of Medicine", "Doctor of Osteopathic Medicine", "Nurse Practitioner", "Physician Assistant", "Administrator", "Other"];
  }, [options.roles]);

  const titleOptions = useMemo(() => {
    const list = toOptionStrings(options.titles);
    return list.length > 0 ? list : ["MD", "DO", "NP", "PA", "Others"];
  }, [options.titles]);

  const onboardingComplete = Boolean(profile?.onboardingComplete);
  const normalizedSpecialty = typeof formValues.specialty === "string" ? formValues.specialty.trim() : "";

  const previewFirstName = formValues.firstName?.trim();
  const previewLastName = formValues.lastName?.trim();
  const previewTitle = formValues.title?.trim();
  const previewFullName = [previewFirstName, previewLastName].filter(Boolean).join(" ");
  const previewName = previewFullName || "Your Name";
  const signatureParts = [previewName];
  const hasOthersTitle = previewTitle && (previewTitle.toLowerCase() === "others" || previewTitle.toLowerCase() === "other");
  if (previewTitle && !hasOthersTitle) {
    signatureParts.push(previewTitle);
  }
  const previewSignature = signatureParts.join(", ");

  const soapNotesMap = useMemo(() => options.soapNotes || options.soapNotePresets || {}, [options]);
  const currentTemplateArray = useMemo(() => {
    if (!normalizedSpecialty) return soapNotesMap?.default || [];
    const key = Object.keys(soapNotesMap || {}).find(
      (k) => String(k).toLowerCase() === normalizedSpecialty.toLowerCase()
    );
    const preset = key ? soapNotesMap[key] : undefined;
    const fallback = soapNotesMap?.default || [];
    return Array.isArray(preset) && preset.length ? preset : Array.isArray(fallback) ? fallback : [];
  }, [soapNotesMap, normalizedSpecialty]);

  const currentTemplateArrayWithOnlyHpi = useMemo(() => {
    return currentTemplateArray.map(section => {
      if (section && section.title === "Subjective") {
        return {
          ...section,
          items: ["History of Present Illness"]
        };
      }
      return section;
    });
  }, [currentTemplateArray]);

  const hpiPreviewText = useMemo(() => {
    const spec = normalizedSpecialty || "default";
    const hit = Object.keys(hpiPreviews).find(k => k.toLowerCase() === spec.toLowerCase());
    const pack = hit ? hpiPreviews[hit] : hpiPreviews.default;
    return formValues.hpiDepthLevel === 1 ? pack.concise : formValues.hpiDepthLevel === 2 ? pack.balanced : pack.detailed;
  }, [normalizedSpecialty, formValues.hpiDepthLevel]);

  useEffect(() => {
    (async () => {
      try {
        const data = await settingsService.getSettingsOptions();
        setOptions(data || {});
      } catch (error) {
        console.error("Load onboarding options error:", error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!currentUser && !loading) {
      refreshMe?.();
    }
  }, [currentUser, loading, refreshMe]);

  useEffect(() => {
    if (currentUser && onboardingComplete && step !== 3) {
      navigate("/scribe", { replace: true });
    }
  }, [currentUser, onboardingComplete, navigate, step]);

  useEffect(() => {
    if (!currentUser?.profile) return;
    const p = currentUser.profile;
    setFormValues((prev) => ({
      ...prev,
      title: p.title || prev.title,
      firstName: p.firstName || prev.firstName,
      lastName: p.lastName || prev.lastName,
      specialty: p.specialty || prev.specialty,
      roleTitle: p.roleTitle || prev.roleTitle,
      companySize: p.companySize || prev.companySize,
      ehrSystem: p.ehrSystem || prev.ehrSystem,
      address: p.address || prev.address,
      city: p.city || prev.city,
      state: p.state || prev.state,
      zipCode: p.zipCode || prev.zipCode,
      country: p.country || prev.country,
      practicePartners: typeof p.practicePartners === "string"
        ? JSON.parse(p.practicePartners)
        : Array.isArray(p.practicePartners)
          ? p.practicePartners
          : prev.practicePartners,
      hpiDepthLevel: p.hpiDepthLevel || prev.hpiDepthLevel || 3,
    }));
  }, [currentUser?.profile?.firstName, currentUser?.profile?.lastName, currentUser?.profile?.country, currentUser?.profile]);

  const handleSelect = (field) => (value) => setFormValues((prev) => ({ ...prev, [field]: value }));
  const handleInput = (field) => (e) => setFormValues((prev) => ({ ...prev, [field]: e.target.value }));

  const requireFields = (keys) => {
    for (const k of keys) {
      const v = formValues[k];
      if (typeof v !== "string" || !v.trim()) {
        return k;
      }
    }
    return null;
  };

  const onNext = () => {
    const missing = requireFields(["firstName", "lastName", "specialty", "title"]);
    if (missing) {
      const labels = {
        firstName: "First name",
        lastName: "Last name",
        specialty: "Specialty",
        title: "Title",
      };
      toast.error(`${labels[missing]} is required.`);
      return;
    }
    setStep(1);
  };

  const onNextStep2 = () => {
    const missing = requireFields(["companySize", "ehrSystem"]);
    if (missing) {
      const labels = { companySize: "Number of clinicians", ehrSystem: "EHR" };
      toast.error(`${labels[missing]} is required.`);
      return;
    }
    setStep(2);
  };

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const countries = useMemo(() => Country.getAllCountries().map(c => ({ label: c.name, value: c.isoCode, ...c })), []);
  const states = useMemo(() => selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode).map(s => ({ label: s.name, value: s.isoCode, ...s })) : [], [selectedCountry]);
  const cities = useMemo(() => selectedState ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map(c => ({ label: c.name, value: c.name, ...c })) : [], [selectedCountry, selectedState]);

  const handleGrabLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    const toastId = toast.loading("Locating...");
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const { latitude, longitude } = position.coords;
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&accept-language=en`);
        const data = await res.json();

        if (data && data.address) {
          const a = data.address;
          const newCountry = a.country;
          const newState = a.state;
          const newCity = a.city || a.town || a.village || a.municipality || a.county || "";
          const newZip = a.postcode || "";
          const road = a.road || a.pedestrian || a.street || "";
          const house_number = a.house_number || "";
          const area = a.suburb || a.neighbourhood || a.quarter || a.residential || "";

          let newAddress = `${house_number} ${road}`.trim();
          if (!newAddress && area) {
            newAddress = area;
          } else if (newAddress && area) {
            newAddress = `${newAddress}, ${area}`;
          }
          const countryCode = a.country_code?.toUpperCase();

          const foundCountry = countries.find(c => c.isoCode === countryCode) || countries.find(c => c.name.toLowerCase() === newCountry?.toLowerCase());
          let foundState = null;

          if (foundCountry) {
            setSelectedCountry(foundCountry);
            const countryStates = State.getStatesOfCountry(foundCountry.isoCode);
            foundState = countryStates.find(s => s.name.toLowerCase() === newState?.toLowerCase() || s.isoCode === newState);
            if (foundState) setSelectedState(foundState);
          }

          setFormValues(prev => ({
            ...prev,
            address: newAddress,
            country: newCountry || "",
            state: newState || "",
            city: newCity || "",
            zipCode: newZip || ""
          }));

          toast.success("Location updated!", { id: toastId });
        } else {
          toast.error("Unable to retrieve your location.", { id: toastId });
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        toast.error("Failed to fetch address.", { id: toastId });
      }
    }, (err) => {
      console.error(err);
      toast.error("Location access denied.", { id: toastId });
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!formValues.title?.trim()) {
      return toast.error("Title is required.");
    }

    const locFields = {
      address: formValues.address?.trim(),
      city: formValues.city?.trim(),
      state: formValues.state?.trim(),
      zipCode: formValues.zipCode?.trim(),
      country: formValues.country?.trim()
    };

    const missingLoc = Object.entries(locFields).find(([k, v]) => !v);
    if (missingLoc) {
      const labels = {
        address: "Address Line",
        city: "City",
        state: "State / Province",
        zipCode: "Zip Code",
        country: "Country"
      };
      return toast.error(`${labels[missingLoc[0]]} is required.`);
    }

    const validPartners = formValues.practicePartners
      .filter(p => p && p.trim().length > 0)
      .map(p => p.trim());

    setSubmitting(true);
    setStep(3); // Show loading state screen

    try {
      const payload = {
        title: formValues.title ? formValues.title.trim() : null,
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        specialty: formValues.specialty ? formValues.specialty.trim() : null,
        roleTitle: formValues.roleTitle ? formValues.roleTitle.trim() : null,
        companySize: formValues.companySize || null,
        ehrSystem: formValues.ehrSystem ? formValues.ehrSystem.trim() : null,
        practicePartners: validPartners.length > 0 ? validPartners : null,
        hpiDepthLevel: formValues.hpiDepthLevel || 3,
        onboardingComplete: true,
      };

      const hasLocation = formValues.address?.trim() || formValues.city?.trim() || formValues.state?.trim() || formValues.zipCode?.trim() || formValues.country?.trim();
      if (hasLocation) {
        payload.address = formValues.address?.trim() || null;
        payload.city = formValues.city?.trim() || null;
        payload.state = formValues.state?.trim() || null;
        payload.zipCode = formValues.zipCode?.trim() || null;
        payload.country = formValues.country?.trim() || null;
      }

      const response = await updateProfileApi(payload);

      // Persist scrape job ID to localStorage so the Practice Partners Database can resume polling it
      if (response && response.data && response.data.scrapeJobId) {
        localStorage.setItem("scrapeJobId", response.data.scrapeJobId);
        localStorage.setItem("scrapeFormNumber", "1");
      }

      // Artificial delay for the "Building your experience" animation
      setTimeout(async () => {
        await refreshMe();
        // Onboarding completion is the first product visit for this account.
        // Clear any stale browser flag so the welcome demo/live choice opens.
        localStorage.removeItem("hasSeenWelcomeModal");
        localStorage.removeItem("activeSessionId");
        localStorage.removeItem("lastSessionId");
        toast.success("You're all set!");
        navigate("/scribe", { replace: true });
      }, 4000);

    } catch (error) {
      setStep(2); // Revert on error
      const message =
        error?.response?.data?.body?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to save your details right now.";
      toast.error(message);
      setSubmitting(false);
    }
  };

  if (loading && !currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-blue-500/60 border-t-transparent" />
      </div>
    );
  }

  // --- Views ---

  if (step === 3) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-100 via-teal-100 to-blue-100 bg-[length:200%_200%] animate-gradient p-4 sm:p-6 lg:p-12">
        <style>{`
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-gradient {
            animation: gradient 8s ease infinite;
          }
        `}</style>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-xl rounded-2xl bg-white p-8 shadow-2xl sm:p-12"
        >
          <BirdLogo />
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
            Building your <span className="text-blue-600">{normalizedSpecialty || "Medical"}</span> experience
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            VirtualScribe is now tailoring its medical knowledge to {normalizedSpecialty ? normalizedSpecialty.toLowerCase() : "medical"} terminology and documentation standards.
          </p>

          <div className="mt-10 space-y-6">
            {[
              `Incorporated ${normalizedSpecialty ? normalizedSpecialty.toLowerCase() : "medical"} terminology`,
              `Added ${normalizedSpecialty ? normalizedSpecialty.toLowerCase() : "medical"} documentation guidelines`,
              // `Configured ${normalizedSpecialty ? normalizedSpecialty.toLowerCase() : "medical"} templates` // feature 
            ].map((text, i) => (
              <motion.div
                key={text}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + (i * 0.8), duration: 0.5 }}
                className="flex items-center gap-3"
              >
                <CheckCircle2 className="h-5 w-5 text-teal-500" />
                <span className="text-sm font-medium text-slate-700">{text}</span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3, duration: 0.5 }}
            className="mt-12 text-sm text-slate-400"
          >
            Taking you to your personalized experience...
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <section className="flex min-h-screen lg:h-screen w-full bg-white lg:overflow-hidden">
      {/* Left Column (Form) */}
      <div className="flex w-full flex-col justify-between px-6 py-5 sm:px-10 lg:w-[50%] xl:w-[50%] lg:px-10 xl:pl-16 xl:pr-10 lg:h-full lg:overflow-y-auto">
        <div>
          {step === 0 && <BirdLogo />}
          {(step === 1 || step === 2) && (
            <button
              onClick={() => setStep(step - 1)}
              className="mb-4 flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}

          <div className="mb-5 sm:mb-6 space-y-2">
            <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl leading-tight">
              {step === 0 ? "Hello, welcome to VirtualScribe" : "Let's tailor your experience"}
            </h1>
            {step === 0 && (
              <p className="text-sm sm:text-[15px] leading-relaxed text-slate-600">
                VirtualScribe is your AI-powered medical scribe that writes notes for you. Let's get started with some information to personalize your experience.
              </p>
            )}
          </div>

          <form onSubmit={onSubmit} className="space-y-4 sm:space-y-4.5">
            {step === 0 && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4 sm:space-y-4.5"
              >
                <div>
                  <Label htmlFor="firstName" className="text-sm sm:text-base font-normal text-slate-800">What is your first name? <span className="text-red-500">*</span></Label>
                  <Input id="firstName" value={formValues.firstName} onChange={handleInput("firstName")} className="mt-1.5 h-11 sm:h-11.5 rounded-xl text-sm sm:text-base font-medium px-4 text-slate-900 shadow-sm" placeholder="Jordan" />
                </div>

                <div>
                  <Label htmlFor="lastName" className="text-sm sm:text-base font-normal text-slate-800">What is your last name? <span className="text-red-500">*</span></Label>
                  <Input id="lastName" value={formValues.lastName} onChange={handleInput("lastName")} className="mt-1.5 h-11 sm:h-11.5 rounded-xl text-sm sm:text-base font-medium px-4 text-slate-900 shadow-sm" placeholder="Chen" />
                </div>

                <div>
                  <Label className="text-sm sm:text-base font-normal text-slate-800">What's your specialty? <span className="text-red-500">*</span></Label>
                  <SearchableCombobox
                    options={specialtyOptions}
                    value={formValues.specialty || ""}
                    onChange={handleSelect("specialty")}
                    placeholder="Search or type your specialty"
                    itemLabel="specialty"
                  />
                </div>

                <div>
                  <Label htmlFor="title" className="text-sm sm:text-base font-normal text-slate-800">What's your title? <span className="text-red-500">*</span></Label>
                  <Select
                    value={formValues.title || undefined}
                    onValueChange={(value) => {
                      setFormValues((prev) => {
                        const next = { ...prev, title: value };
                        if (value === "MD") next.roleTitle = "Doctor of Medicine";
                        else if (value === "DO") next.roleTitle = "Doctor of Osteopathic Medicine";
                        else if (value === "NP") next.roleTitle = "Nurse Practitioner";
                        else if (value === "PA") next.roleTitle = "Physician Assistant";
                        else if (value === "Others" || value === "Other") next.roleTitle = "Administrator";
                        else next.roleTitle = "";
                        return next;
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1.5 h-11 sm:h-11.5 rounded-xl border border-slate-200 bg-white px-4 text-sm sm:text-base font-medium text-slate-900 shadow-sm focus:ring-1 focus:ring-blue-500">
                      <SelectValue placeholder="Select Title" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1">
                      {titleOptions.map((titleOpt) => (
                        <SelectItem key={titleOpt} value={titleOpt} className="px-3.5 py-2 text-sm sm:text-sm hover:bg-blue-100 cursor-pointer">
                          {titleOpt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="button"
                  onClick={onNext}
                  className="mt-5 flex h-11 sm:h-11.5 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm sm:text-base font-semibold text-white shadow-md hover:bg-blue-700 sm:w-auto sm:px-8"
                >
                  Next <ArrowRight className="h-4.5 w-4.5" />
                </Button>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4 sm:space-y-4.5"
              >
                <div>
                  <Label className="text-sm sm:text-base font-normal text-slate-800">How many clinicians work in your practice or department? <span className="text-red-500">*</span></Label>
                  <Select value={formValues.companySize || undefined} onValueChange={handleSelect("companySize")}>
                    <SelectTrigger className="mt-1.5 h-11 sm:h-11.5 rounded-xl border border-slate-200 bg-white px-4 text-sm sm:text-base font-medium text-slate-900 shadow-sm focus:ring-1 focus:ring-blue-500">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1">
                      {companySizeOptions.map((option) => (
                        <SelectItem key={option} value={option} className="px-3.5 py-2 text-sm sm:text-sm cursor-pointer">{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm sm:text-base font-normal text-slate-800">What is your EHR? <span className="text-red-500">*</span></Label>
                  <SearchableCombobox
                    options={ehrOptions}
                    value={formValues.ehrSystem || ""}
                    onChange={handleSelect("ehrSystem")}
                    placeholder="Search or type your EHR"
                    itemLabel="EHR"
                  />
                </div>

                <Button
                  type="button"
                  onClick={onNextStep2}
                  className="mt-5 flex h-11 sm:h-11.5 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm sm:text-base font-semibold text-white shadow-md hover:bg-blue-700 sm:w-auto sm:px-8"
                >
                  Next <ArrowRight className="h-4.5 w-4.5" />
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4 sm:space-y-4.5"
              >
                <div>
                  <Label className="text-sm sm:text-base font-normal text-slate-800">
                    HPI Output Depth <span className="ml-2 text-xs text-slate-400 font-normal">(default: Detailed)</span>
                  </Label>
                  <Select
                    value={String(formValues.hpiDepthLevel || 3)}
                    onValueChange={(value) => setFormValues((prev) => ({ ...prev, hpiDepthLevel: parseInt(value) }))}
                  >
                    <SelectTrigger className="mt-1.5 h-11 sm:h-11.5 rounded-xl border border-slate-200 bg-white px-4 text-sm sm:text-base font-medium text-slate-900 shadow-sm focus:ring-1 focus:ring-blue-500">
                      <SelectValue placeholder="Select HPI Depth" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1">
                      <SelectItem value="1" className="px-3.5 py-2 text-sm sm:text-sm cursor-pointer">Concise HPI (Salient points only, minimal detail)</SelectItem>
                      <SelectItem value="2" className="px-3.5 py-2 text-sm sm:text-sm cursor-pointer">Balanced HPI (Medium depth and detail)</SelectItem>
                      <SelectItem value="3" className="px-3.5 py-2 text-sm sm:text-sm cursor-pointer">Detailed HPI (Comprehensive paragraph form)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1 w-full">
                    <Label htmlFor="address" className="text-sm sm:text-base font-normal text-slate-800">Address Line <span className="text-red-500">*</span></Label>
                    <Input id="address" value={formValues.address} onChange={handleInput("address")} className="mt-1.5 h-11 sm:h-11.5 rounded-xl text-sm sm:text-base font-medium px-4 text-slate-900 shadow-sm w-full" placeholder="123 Main St" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 sm:h-11.5 w-full sm:w-auto shrink-0 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm bg-white flex items-center justify-center gap-2"
                    onClick={handleGrabLocation}
                  >
                    <MapPin className="h-4 w-4 text-blue-600 shrink-0" />
                    Grab Current Location
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-sm sm:text-base font-normal text-slate-800">Country <span className="text-red-500">*</span></Label>
                    <SingleSelectDropdown
                      options={countries}
                      value={selectedCountry ? selectedCountry.isoCode : undefined}
                      placeholder="Select Country"
                      onSelect={(val) => {
                        const c = countries.find(x => x.value === val);
                        setSelectedCountry(c);
                        setSelectedState(null);
                        setSelectedCity(null);
                        setFormValues(prev => ({ ...prev, country: c.name, state: "", city: "" }));
                      }}
                      triggerClassName="mt-1.5 h-11 sm:h-11.5 w-full rounded-xl border border-slate-200 bg-white px-4 text-left text-sm sm:text-base font-medium text-slate-900 shadow-sm"
                      panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl"
                      listClassName="max-h-60 overflow-y-auto py-1"
                      searchInputClassName="w-full text-sm sm:text-sm outline-none placeholder:text-slate-400 py-2 px-1"
                      optionClassName="px-3.5 py-2 text-sm sm:text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-600"
                      emptyStateClassName="px-3.5 py-2 text-sm sm:text-sm text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-sm sm:text-base font-normal text-slate-800">State / Province <span className="text-red-500">*</span></Label>
                    <SingleSelectDropdown
                      options={states}
                      disabled={!selectedCountry}
                      value={selectedState ? selectedState.isoCode : undefined}
                      placeholder={selectedCountry ? "Select State" : "Select Country First"}
                      onSelect={(val) => {
                        const s = states.find(x => x.value === val);
                        setSelectedState(s);
                        setSelectedCity(null);
                        setFormValues(prev => ({ ...prev, state: s.name, city: "" }));
                      }}
                      triggerClassName="mt-1.5 h-11 sm:h-11.5 w-full rounded-xl border border-slate-200 bg-white px-4 text-left text-sm sm:text-base font-medium text-slate-900 shadow-sm"
                      panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl"
                      listClassName="max-h-60 overflow-y-auto py-1"
                      searchInputClassName="w-full text-sm sm:text-sm outline-none placeholder:text-slate-400 py-2 px-1"
                      optionClassName="px-3.5 py-2 text-sm sm:text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-600"
                      emptyStateClassName="px-3.5 py-2 text-sm sm:text-sm text-slate-500"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-sm sm:text-base font-normal text-slate-800">City <span className="text-red-500">*</span></Label>
                    {cities.length > 0 ? (
                      <SingleSelectDropdown
                        options={cities}
                        disabled={!selectedState}
                        value={formValues.city}
                        placeholder={selectedState ? "Select City" : "Select State First"}
                        onSelect={(val) => {
                          setFormValues(prev => ({ ...prev, city: val }));
                        }}
                        triggerClassName="mt-1.5 h-11 sm:h-11.5 w-full rounded-xl border border-slate-200 bg-white px-4 text-left text-sm sm:text-base font-medium text-slate-900 shadow-sm"
                        panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl"
                        listClassName="max-h-60 overflow-y-auto py-1"
                        searchInputClassName="w-full text-sm sm:text-sm outline-none placeholder:text-slate-400 py-2 px-1"
                        optionClassName="px-3.5 py-2 text-sm sm:text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-600"
                        emptyStateClassName="px-3.5 py-2 text-sm sm:text-sm text-slate-500"
                      />
                    ) : (
                      <Input
                        id="city"
                        value={formValues.city}
                        onChange={handleInput("city")}
                        className="mt-1.5 h-11 sm:h-11.5 rounded-xl text-sm sm:text-base font-medium px-4 text-slate-900 shadow-sm"
                        placeholder={selectedState ? "Enter City" : "Select State First"}
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="zipCode" className="text-sm sm:text-base font-normal text-slate-800">Zip Code <span className="text-red-500">*</span></Label>
                    <Input id="zipCode" value={formValues.zipCode} onChange={handleInput("zipCode")} className="mt-1.5 h-11 sm:h-11.5 rounded-xl text-sm sm:text-base font-medium px-4 text-slate-900 shadow-sm" placeholder="10001" />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="mt-5 flex h-11 sm:h-11.5 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm sm:text-base font-semibold text-white shadow-md hover:bg-blue-700 disabled:opacity-70 sm:w-auto sm:px-8"
                >
                  Get started <ArrowRight className="h-4.5 w-4.5" />
                </Button>
              </motion.div>
            )}
          </form>
        </div>

        <div className="mt-6 pb-2">
          <a href="https://virtualscribe.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-slate-400 hover:text-slate-600">Privacy Policy</a>
        </div>
      </div>

      {/* Right Column (Dynamic Preview) */}
      <div className="hidden lg:block lg:flex-1 py-4 lg:py-6 pl-4 lg:pl-6 pr-0 lg:h-full">
        <style>{`
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-gradient {
            animation: gradient 8s ease infinite;
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
        <div className="relative flex h-full w-full items-center justify-end overflow-hidden rounded-l-[2rem] bg-gradient-to-br from-blue-600 via-teal-500 to-blue-700 bg-[length:200%_200%] animate-gradient shadow-inner">
          <AnimatePresence mode="wait">
            {step === 2 ? (
              <motion.div
                key="step-3-info"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-[650px] rounded-l-2xl bg-white/95 backdrop-blur-md shadow-2xl flex flex-col max-h-[90vh]"
              >
                <div className="p-8 pb-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-xl font-medium text-slate-900">
                    {normalizedSpecialty ? (
                      <>New <span className="font-semibold text-blue-600">{normalizedSpecialty}</span> Note</>
                    ) : (
                      "New Note"
                    )}
                  </h3>
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                    Sample Preview
                  </span>
                </div>

                <div className="p-8 pt-4 space-y-6 overflow-y-auto no-scrollbar">
                  {currentTemplateArrayWithOnlyHpi.map((section, idx) => (
                    <div key={idx} className={section.title === "Assessment & Plan" ? "border-b border-slate-100 pb-6" : ""}>
                      <h4 className="mb-3 text-sm font-semibold text-slate-900">{section.title}</h4>
                      {section.title === "Subjective" ? (
                        <div className="space-y-2">
                          <h5 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">History of present illness</h5>
                          <p className="text-sm text-slate-600 leading-relaxed font-sans bg-slate-50 border border-slate-100 p-4 rounded-xl">
                            {hpiPreviewText}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {section.items.map(pill => (
                            <span key={pill} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600">{pill}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Signature */}
                  <div className="pt-2 pb-4">
                    <p className="text-sm text-slate-600">Signed,</p>
                    <p className="mt-2 inline-block border-b border-slate-300 pb-1 pr-12 text-lg text-slate-900 font-medium">
                      {previewSignature}
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="preview-card"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-[650px] rounded-l-2xl bg-white/95 backdrop-blur-md shadow-2xl flex flex-col max-h-[90vh]"
              >
                <div className="p-8 pb-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-xl font-medium text-slate-900">
                    {normalizedSpecialty ? (
                      <>New <span className="font-semibold text-blue-600">{normalizedSpecialty}</span> Note</>
                    ) : (
                      "New Note"
                    )}
                  </h3>
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                    Sample Preview
                  </span>
                </div>

                <div className="p-8 pt-4 space-y-6 overflow-y-auto no-scrollbar">
                  {!normalizedSpecialty ? (
                    <div className="space-y-8 animate-pulse pb-4">
                      {[1, 2].map((sectionIdx) => (
                        <div key={sectionIdx} className="space-y-4">
                          <div className="h-5 w-24 rounded bg-slate-200"></div>
                          <div className="space-y-3">
                            {[1, 2, 3].map((lineIdx) => (
                              <div key={lineIdx} className="flex items-center justify-between">
                                <div className="h-4 w-[60%] rounded bg-slate-200"></div>
                                <div className="flex gap-2">
                                  <div className="h-4 w-10 rounded bg-slate-200"></div>
                                  <div className="h-4 w-10 rounded bg-slate-200"></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    currentTemplateArray.map((section, idx) => (
                      <div key={idx} className={section.title === "Assessment & Plan" ? "border-b border-slate-100 pb-6" : ""}>
                        <h4 className="mb-3 text-sm font-semibold text-slate-900">{section.title}</h4>
                        <div className="flex flex-wrap gap-2">
                          {section.items.map(pill => (
                            <span key={pill} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600">{pill}</span>
                          ))}
                        </div>
                      </div>
                    ))
                  )}

                  {/* Signature */}
                  <div className="pt-2 pb-4">
                    <p className="text-sm text-slate-600">Signed,</p>
                    <p className="mt-2 inline-block border-b border-slate-300 pb-1 pr-12 text-lg text-slate-900">
                      {previewSignature}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}